import { useNavigate } from '@tanstack/react-router'
import { produce } from 'immer'
import { useCallback, useEffect, useMemo } from 'react'
import getRootPath from '~utils/app/get-root-path'
import { deepCloneValueToValue } from '~utils/general'
import ConsultationLayout from './components/layout'
import { useConsultationScreens } from './hooks/use-consultation-screens'
import { getLegacyPages, MAIN_CONCERN_SCREEN } from './screens'
import ConcernTile from './components/ui/concern-tile'
import { concernIconName, getConcernIcon } from './components/concern-icons'
import { loadedData } from './data'
import { useConsultationData } from './store'
import { isConsultationProcess } from './types'
import { getGroupItemDisplayValue } from './utils'
import Mixpanel from '~utils/mixpanel'
import useIntakeSpec, { getIntakeSpecV2 } from '~repositories/hooks/use-intake-spec'

// 메뉴 한 칸. isNew 면 others 문진 + category 로 보낸다 (DRT-7113)
type ConcernTileItem = {
  value: string
  label: string
  keywords?: string
  description?: string
  icon?: string
  isNew: boolean
}

const TITLE_ID = 'main-concern-title'
const PICKER_HINT = 'Pick the one that fits best. You can add details later.'

/**
 * @version v1.0.0
 * @author Carey
 */
const MainConcern = () => {
  const navigate = useNavigate()
  const [consultationData, setConsultationData] = useConsultationData()
  const { data: spec, isPending: specPending } = useIntakeSpec()
  // 신규 증상 6종 (DRT-7113): 고르면 others 문진 + category 로 보낸다
  const selectedCategory = (consultationData.checkup?.others as Record<string, unknown> | null)
    ?.category as string | undefined

  const onSelectNewConcern = useCallback(
    (category: string) => {
      setConsultationData(
        produce(data => {
          const fulfilled = Object.assign(
            data,
            deepCloneValueToValue(loadedData, {}, isConsultationProcess)
          )
          for (const concernCandidate of Object.keys(fulfilled.checkup)) {
            if (concernCandidate !== 'others') {
              fulfilled.checkup[concernCandidate] = null
            }
          }
          ;(fulfilled.checkup.others as Record<string, unknown>).category = category
          // 증상이 바뀌면 이전 증상에서 띄운 응급 안내 단계는 잊는다 (새 답으로 다시 판단)
          fulfilled.intake_alert_level = null
          fulfilled.intake_text_unknown_ids = []
          fulfilled.intake_prefilled_ids = []
          return fulfilled
        })
      )
    },
    [setConsultationData]
  )

  const currentValue = useMemo(() => {
    if (!consultationData.checkup) return null

    const selectedCheckupKeyValue = Object.entries(consultationData.checkup).find(
      ([_, value]) => value !== null
    )

    if (!selectedCheckupKeyValue) return null

    return selectedCheckupKeyValue[0]
  }, [consultationData.checkup])

  // 아직 증상을 고르지 않았으면 기타 경로 기준 (진행 바 전체 화면 수)
  const screens = useConsultationScreens(currentValue ?? undefined)

  const onSelect = useCallback(
    (_selectedMainConcern: string) => {
      setConsultationData(
        produce(data => {
          const fulfilledConsultationData = Object.assign(
            data,
            deepCloneValueToValue(loadedData, {}, isConsultationProcess)
          )

          for (const concernCandidate of Object.keys(fulfilledConsultationData.checkup)) {
            if (concernCandidate !== _selectedMainConcern) {
              fulfilledConsultationData.checkup[concernCandidate] = null
            }
          }
          const others = fulfilledConsultationData.checkup.others as Record<string, unknown> | null
          if (others) delete others.category
          // 증상이 바뀌면 이전 증상에서 띄운 응급 안내 단계는 잊는다 (새 답으로 다시 판단)
          fulfilledConsultationData.intake_alert_level = null
          fulfilledConsultationData.intake_text_unknown_ids = []
          fulfilledConsultationData.intake_prefilled_ids = []

          return fulfilledConsultationData
        })
      )
    },
    [setConsultationData]
  )

  const onNextPress = useCallback(() => {
    if (!currentValue) {
      throw new Error('No main concern datafields')
    }

    const currentMainConcernDatafields = consultationData.checkup?.[currentValue]
    if (!currentMainConcernDatafields) {
      throw new Error('No valid concern datafields')
    }

    // 증상별 화면이 없으면(기타·신규 증상) 바로 안전 질문.
    // 문진 v2 는 증상별 기존 화면을 쓰지 않고 질문지 흐름(flows)을 한 질문씩 보여 준다 (DRT-7186)
    const isV2 = !!getIntakeSpecV2(spec)
    const startPage = isV2 ? undefined : getLegacyPages(currentValue)[0]
    // 이 상담의 문진 버전을 여기서 정한다. 이후 화면·제출은 질문지가 바뀌어도 이 버전을 따른다
    setConsultationData(
      produce(draft => {
        draft.intake_version = isV2 ? 2 : 1
      })
    )

    navigate({
      to: startPage
        ? `${getRootPath()}/ask-a-vet/preliminary-checkup/${currentValue}/${startPage}/`
        : `${getRootPath()}/ask-a-vet/safety/${currentValue}`,
      resetScroll: true,
    })
  }, [consultationData.checkup, currentValue, navigate, setConsultationData, spec])

  const legacyConcerns = useMemo(() => Object.keys(loadedData.checkup), [])

  const concernTiles = useMemo<ConcernTileItem[]>(() => {
    // 서버가 메뉴(concern_meta)를 주면 그 순서·문구·아이콘 그대로 (DRT-7185)
    const meta = spec?.concern_meta
    if (meta?.length) {
      return (
        meta
          // 이 앱이 모르는 기존 증상 값은 문진을 이어 갈 수 없어 뺀다
          .filter(concern => concern.is_new || legacyConcerns.includes(concern.value))
          .map(concern => ({
            value: concern.value,
            label: concern.label,
            keywords: concern.keywords,
            description: concern.description,
            icon: getConcernIcon(concern.icon),
            isNew: concern.is_new,
          }))
      )
    }
    // 예전 서버: 기존 증상 + 신규 증상(new_concerns)을 concern_order 대로
    const legacy = legacyConcerns.map<ConcernTileItem>(concern => ({
      value: concern,
      label: getGroupItemDisplayValue({ value: concern, label: concern }),
      icon: getConcernIcon(concernIconName(concern)),
      isNew: false,
    }))
    const fresh = (spec?.new_concerns ?? []).map<ConcernTileItem>(concern => ({
      value: concern.value,
      label: concern.label,
      description: concern.description,
      icon: getConcernIcon(concernIconName(concern.value)),
      isNew: true,
    }))
    // 예전 순서: 기존 증상(기타 제외) → 신규 증상 → 기타
    const rows = [
      ...legacy.filter(row => row.value !== 'others'),
      ...fresh,
      ...legacy.filter(row => row.value === 'others'),
    ]
    const order = spec?.concern_order
    if (!order?.length) return rows
    const rank = (key: string) => {
      // 기타는 서버 순서와 상관없이 항상 맨 끝
      if (key === 'others') return Number.MAX_SAFE_INTEGER
      const index = order.indexOf(key)
      return index === -1 ? order.length : index
    }
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => rank(a.row.value) - rank(b.row.value) || a.index - b.index)
      .map(({ row }) => row)
  }, [legacyConcerns, spec])

  // 고른 칸: 신규 증상이면 category, 아니면 고른 기존 증상 (기타와 신규 증상이 같이 골라져 보이지 않게)
  const selectedValue = selectedCategory ?? currentValue
  const selectedTile = concernTiles.find(tile => tile.value === selectedValue)

  useEffect(() => {
    Mixpanel.logEvent('askavet: started', {
      is_user_scenario: true,
    })
  }, [])

  return (
    <ConsultationLayout
      title={`What is your\nmain concern today?`}
      titleId={TITLE_ID}
      // 질문지를 받기 전에는 v1/v2 를 정할 수 없어 기다린다 (오프라인에서 문진을 건너뛰지 않게)
      // 저장된 증상이 지금 목록에 없으면(보이는 선택 없음) 넘어가지 못하게 한다
      nextButtonDisabled={!selectedTile || specPending}
      onNextPress={onNextPress}
      progress={screens && { current: MAIN_CONCERN_SCREEN, total: screens.total }}
      required
      footer={
        // 고른 증상 설명 (없으면 안내). 두 줄 높이를 잡아 둬서 골라도 버튼이 움직이지 않는다
        <p className="min-h-10 text-body-3 text-gray-700 dark:text-gray-500" aria-live="polite">
          {selectedTile?.description || PICKER_HINT}
        </p>
      }
    >
      <section className="w-full pb-2">
        {/* 기존·신규 증상을 서버 순서대로 두 칸 타일로 보여 준다 (DRT-7185).
            미리 골라 두지 않는다(맨 위로 쏠려 통계가 틀어지고 가이드도 미리 선택을 금지).
            질문지를 받기 전에는 목록이 재배치되며 잘못 누르지 않게 비워 둔다.
            오래된 웹뷰(Chrome 64)는 flex gap 을 못 써서 칸마다 안쪽 여백으로 간격을 만든다 */}
        <div className="flex flex-row flex-wrap -m-1" role="group" aria-labelledby={TITLE_ID}>
          {!specPending &&
            concernTiles.map(tile => (
              <div key={tile.value} className="flex w-1/2 p-1">
                <ConcernTile
                  label={tile.label}
                  keywords={tile.keywords}
                  icon={tile.icon}
                  isSelected={selectedValue === tile.value}
                  onSelect={() => {
                    // 이미 고른 칸을 다시 누르면 아무것도 하지 않는다 (다시 고르면 증상 답이 지워진다)
                    if (selectedValue === tile.value) return
                    if (tile.isNew) onSelectNewConcern(tile.value)
                    else onSelect(tile.value)
                  }}
                />
              </div>
            ))}
        </div>
      </section>
    </ConsultationLayout>
  )
}

MainConcern.displayName = 'MainConcernScreen'

export default MainConcern
